import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
// Importiamo i componenti Geist corretti
import { GeistProvider, CssBaseline, Themes } from '@geist-ui/core';
import { Moon, Sun } from '@geist-ui/icons';
// Importa il tema personalizzato
import { materiaTheme } from './config/geist';
// Importa il componente del cursore personalizzato
import CustomCursor from './components/CustomCursor';

// Import page components
import Page1 from './page1/index.jsx';
import Page2 from './page2/index.jsx';
import Page3 from './page3/index.jsx';
import SimilarityFinderPage from './components/SimilarityFinderPage';

// Creo un tema scuro personalizzato basato sul grigio
const darkTheme = Themes.createFromLight({
  type: 'customDark',
  palette: {
    background: '#222',
    accents_1: '#333',
    accents_2: '#444',
    accents_3: '#555',
    accents_4: '#666',
    accents_5: '#888',
    accents_6: '#999',
    accents_7: '#eaeaea',
    accents_8: '#fff',
    foreground: '#fff',
  },
  // Usa lo stesso font del materiaTheme
  font: materiaTheme.font
});

// Componente per il theme switcher
const ThemeSwitcher = ({ onClick, isDark }) => {
  return (
    <div 
      onClick={onClick}
      className={`theme-switcher-button ${isDark ? 'dark' : ''}`}
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        background: isDark ? '#444' : '#eaeaea',
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        zIndex: 2000,
        boxShadow: isDark 
          ? '0 0 15px rgba(100, 100, 255, 0.3), 0 0 5px rgba(150, 150, 255, 0.2)' 
          : '0 5px 10px rgba(0, 0, 0, 0.15)'
      }}
    >
      {isDark ? <Sun size={22} /> : <Moon size={22} />}
    </div>
  );
};

// Componente per animare le transizioni di pagina
const AnimatedRoutes = () => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");
  
  useEffect(() => {
    // Se la location è cambiata
    if (location.pathname !== displayLocation.pathname) {
      setTransitionStage("fadeOut");
      // Attendiamo la fine dell'animazione di fadeOut
      const timeout = setTimeout(() => {
        setDisplayLocation(location);
        setTransitionStage("fadeIn");
      }, 200); // Tempo leggermente inferiore alla durata della transizione
      
      return () => clearTimeout(timeout);
    }
  }, [location, displayLocation]);
  
  return (
    <div className={`page-transition ${transitionStage}`}
      style={{
        opacity: transitionStage === "fadeIn" ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out', 
        height: '100%'
      }}
    >
      <Routes location={displayLocation}>
        <Route path="/" element={<Page1 />} />
        <Route path="/page1" element={<Page1 />} />
        <Route path="/page2" element={<Page2 />} />
        <Route path="/page3" element={<Page3 />} />
        <Route path="/similarity" element={<SimilarityFinderPage />} />
      </Routes>
    </div>
  );
};

function App() {
  const [themeType, setThemeType] = useState('light');
  
  const toggleTheme = () => {
    setThemeType(prev => prev === 'light' ? 'customDark' : 'light');
  };

  // Applica la classe del tema all'elemento HTML quando cambia il tema
  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    
    if (themeType === 'customDark') {
      htmlElement.classList.add('geist-customDark');
      bodyElement.classList.add('geist-customDark');
    } else {
      htmlElement.classList.remove('geist-customDark');
      bodyElement.classList.remove('geist-customDark');
    }

    // Aggiorna il titolo della pagina
    document.title = "KOSMA.ZIP";
  }, [themeType]);

  return (
    <GeistProvider themes={[darkTheme, materiaTheme]} themeType={themeType}>
      <CssBaseline />
      <CustomCursor />
      <ThemeSwitcher onClick={toggleTheme} isDark={themeType === 'customDark'} />
      <Router>
        <div className="App">
          <AnimatedRoutes />
        </div>
      </Router>
    </GeistProvider>
  );
}

export default App; 